import { Modal } from '@/components/shared/Modal'
import { Button } from '@/components/shared/Button'
import { Spinner } from '@/components/shared/Spinner'
import { useState } from 'react'
import { fetchAPI } from '@/api/client'
import type { RequestCapResponse, RequestCapRequest } from '@/types/api'
import { OverflowQualityWarning } from './OverflowQualityWarning'

interface RequestCapModalProps {
  open: boolean
  onClose: () => void
  geoId: number
  geoCode: string
  geoName: string
  /** Modal title override (e.g. public overflow). */
  title?: string
  /** Cap vs overflow vs permanent — quality warning shown only for overflow requests near reset. */
  requestKind?: 'cap' | 'overflow' | 'permanent'
  /** The offer's cap unit — drives the input label. Affiliates enter the NET amount they
   *  want added; the backend reverses their throttle to the gross cap. Defaults to 'usd'. */
  unit?: 'usd' | 'conversions'
  /** Endpoint to POST the request to. Defaults to the authed route; the share link
   *  passes its token endpoint so requests work without a login. */
  requestPath?: string
  /** Set while the offer's pre-day window is open (Reco, from 9 PM offer-local): the
   *  offer-local date this request will be FOR. Renders the "request for tomorrow" note. */
  preRequestForDate?: string | null
}

export function RequestCapModal({
  open,
  onClose,
  geoId,
  geoCode,
  geoName,
  title = 'Request More Cap',
  requestKind = 'cap',
  unit = 'usd',
  requestPath = '/api/cap/request',
  preRequestForDate = null,
}: RequestCapModalProps) {
  const isConv = unit === 'conversions'
  const amountLabel = isConv ? 'Additional conversions' : 'Additional cap amount ($)'
  const [amount, setAmount] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The server's answer is authoritative about WHICH day the request landed on — the
  // window can flip between render and submit, so the confirmation reads the response.
  const [submittedFor, setSubmittedFor] = useState<string | null>(null)

  const parsedAmount = Math.round(Number(amount) || 0)
  const isValid = parsedAmount > 0

  const handleSubmit = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetchAPI<RequestCapResponse>(requestPath, {
        method: 'POST',
        body: JSON.stringify({
          geo_id: geoId,
          amount: parsedAmount,
          ...(requestKind === 'overflow' || requestKind === 'permanent'
            ? { request_kind: requestKind }
            : {}),
        } satisfies RequestCapRequest),
      })
      setSubmittedFor(res.target_local_date ?? null)
      setSubmitted(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setAmount('')
    setSubmitted(false)
    setSubmittedFor(null)
    setError(null)
    onClose()
  }

  return (
    <Modal open={open} onClose={handleClose} title={title}>
      {submitted ? (
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-brand-green/8 border border-brand-green/20">
            <p className="text-sm text-brand-green">
              Request for <strong>{geoCode}</strong> submitted.
              {submittedFor
                ? ` This is a request for tomorrow (${submittedFor}) — if approved, your cap takes effect at the daily reset (12:00 AM offer time), not immediately.`
                : requestKind === 'overflow'
                  ? ' Your account manager will approve geo access and today\'s overflow draw.'
                  : requestKind === 'permanent'
                    ? ' Your account manager will approve geo access and your permanent cap.'
                    : ' Your account manager will be in touch.'}
            </p>
          </div>
          <Button variant="secondary" onClick={handleClose} className="w-full">
            Close
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1.5">Geo</label>
            <div className="w-full px-3 py-2.5 rounded-lg text-sm bg-gray-50 border border-gray-200 text-text-primary">
              {geoCode} — {geoName}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1.5">
              {amountLabel}
            </label>
            <input
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder={isConv ? 'e.g. 50' : 'e.g. 5000'}
              className="w-full px-3 py-2.5 rounded-lg text-sm bg-surface-card border border-gray-300 text-text-primary placeholder:text-text-placeholder shadow-xs focus:outline-none focus:border-brand-blue focus:ring-4 focus:ring-brand-blue/12"
            />
          </div>

          {preRequestForDate && (
            <div className="p-3 rounded-lg bg-brand-blue/8 border border-brand-blue/20">
              <p className="text-xs text-brand-blue">
                <strong>Request for tomorrow ({preRequestForDate}).</strong> The pre-day window is
                open — if approved, your cap takes effect at the daily reset (12:00 AM offer
                time), not immediately.
              </p>
            </div>
          )}

          {requestKind === 'overflow' && <OverflowQualityWarning />}

          {error && (
            <div className="p-3 rounded-lg bg-brand-red/8 border border-brand-red/20">
              <p className="text-xs text-brand-red">{error}</p>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <Button variant="secondary" onClick={handleClose} disabled={isLoading} className="flex-1">
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!isValid || isLoading}
              className="flex-1"
            >
              {isLoading ? <><Spinner className="w-4 h-4" /> Submitting...</> : 'Submit Request'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
